import {
  DecoratorNode,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export type SerializedMentionNode = Spread<
  {
    mentionName: string;
    docId: string;
    docType: string;
  },
  SerializedLexicalNode
>;

export class MentionNode extends DecoratorNode<JSX.Element> {
  __mention: string;
  __docId: string;
  __docType: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__docId, node.__docType, node.__key);
  }

  constructor(mentionName: string, docId: string, docType: string, key?: NodeKey) {
    super(key);
    this.__mention = mentionName;
    this.__docId = docId;
    this.__docType = docType;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = document.createElement('span');
    dom.className = 'mention-node';
    return dom;
  }

  updateDOM(): false {
    return false;
  }

  exportJSON(): SerializedMentionNode {
    return {
      mentionName: this.__mention,
      docId: this.__docId,
      docType: this.__docType,
      type: 'mention',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(
      serializedNode.mentionName,
      serializedNode.docId,
      serializedNode.docType
    );
    return node;
  }

  decorate(): JSX.Element {
    // Determine color based on document type (normalized to uppercase for comparison)
    const normalizedType = this.__docType.toUpperCase();
    let colorClass = 'bg-blue-100 text-blue-700';

    if (normalizedType === 'UPLOAD') {
      colorClass = 'bg-amber-100 text-amber-700';
    } else if (normalizedType === 'MPEP') {
      colorClass = 'bg-emerald-100 text-emerald-700';
    } else if (normalizedType === 'CFR') {
      colorClass = 'bg-cyan-100 text-cyan-700';
    } else if (normalizedType === 'USC') {
      colorClass = 'bg-violet-100 text-violet-700';
    } else if (normalizedType === 'DRAFT') {
      colorClass = 'bg-purple-100 text-purple-700';
    } else if (normalizedType.includes('PROSECUTION')) {
      colorClass = 'bg-orange-100 text-orange-700';
    }

    return (
      <span
        className={`inline-flex items-center ${colorClass} rounded px-1 mx-0.5 font-medium text-sm cursor-pointer select-none`}
        contentEditable={false}
        data-lexical-mention="true"
        data-doc-id={this.__docId}
        data-doc-type={this.__docType}
      >
        {this.__mention}
      </span>
    );
  }

  getTextContent(): string {
    // Return the tag format so it's backend-compatible
    return `[${this.__docType.toUpperCase()}_${this.__docId}]`;
  }
}

export function $createMentionNode(
  mentionName: string,
  docId: string,
  docType: string
): MentionNode {
  return new MentionNode(mentionName, docId, docType);
}

export function $isMentionNode(
  node: LexicalNode | null | undefined
): node is MentionNode {
  return node instanceof MentionNode;
}
